import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

export interface HsnSacCode {
  id: string
  organizationId: string
  code: string
  description: string
  type: 'HSN' | 'SAC'
  taxMasterId: string | null
  isActive: boolean
  taxMaster?: { id: string; name: string; code?: string; treatment?: string; rate: number }
  createdAt: string
  updatedAt: string
}

export interface HsnSacCodeFilters {
  type?: string
  isActive?: boolean
  search?: string
}

export function useHsnSacCodes(filters?: HsnSacCodeFilters) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['hsnSacCodes', organization?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ organizationId: organization?.id || '' })
      if (filters?.type) params.set('type', filters.type)
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
      if (filters?.search) params.set('search', filters.search)
      const res = await api.get<any>(`/master-data/hsn-sac?${params}`)
      return (res?.data || []) as HsnSacCode[]
    },
    enabled: !!organization?.id && can('QUOTATIONS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useHsnSacCodeMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['hsnSacCodes', organization?.id] })
  }

  const create = useMutation({
    mutationFn: (payload: Omit<HsnSacCode, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'taxMaster'>) =>
      api.post<any>('/master-data/hsn-sac', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<HsnSacCode> }) =>
      api.patch<any>(`/master-data/hsn-sac/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/hsn-sac/${id}/status`, { isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/master-data/hsn-sac/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, remove }
}